import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, ArrowRight, ShieldCheck, Calculator, HelpCircle, Table2 } from "lucide-react";

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 });
const PRIO = {
  high: { label: "High priority", cls: "bg-rose-50 text-rose-700 ring-rose-200", bar: "bg-rose-500" },
  medium: { label: "Needs attention", cls: "bg-amber-50 text-amber-800 ring-amber-200", bar: "bg-amber-400" },
  low: { label: "Low", cls: "bg-slate-50 text-slate-600 ring-slate-200", bar: "bg-slate-300" },
  info: { label: "Info", cls: "bg-cyan-50 text-cyan-800 ring-cyan-200", bar: "bg-cyan-400" },
};

export const fmtValue = (v, f) => {
  if (v === null || v === undefined || v === "") return "—";
  if (f === "gbp") return gbp.format(Number(v) || 0);
  if (f === "days") return `${v}d`;
  if (f === "percent") return `${v}%`;
  if (f === "bool") return v ? "Yes" : "No";
  if (f === "list") return Array.isArray(v) ? v.join(", ") : String(v);
  if (f === "date") { const d = new Date(String(v).slice(0, 10)); return isNaN(d) ? String(v) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  if (f === "count" || f === "number") return Number(v).toLocaleString("en-GB");
  return String(v);
};

function Metric({ m, i }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 min-w-0" data-testid={`ted-metric-${i}`}>
      <div className="text-[11px] font-medium leading-tight text-slate-500 line-clamp-2" title={m.label}>{m.label}</div>
      <div className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900 tabular-nums">{fmtValue(m.value, m.format)}</div>
      {!m.verified && <div className="text-[10px] font-medium text-amber-700" data-testid={`ted-metric-unverified-${i}`}>Unverified</div>}
    </div>
  );
}

function Cell({ c, row, onNavigate }) {
  const v = row[c.key];
  if (c.format === "link" && row._href && v) return <Link to={row._href} onClick={onNavigate} className="font-medium text-[var(--ted-violet)] hover:underline" data-testid={`ted-table-link-${v}`}>{v}</Link>;
  if (c.format === "priority") return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${/urgent|high/i.test(v || "") ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{v || "—"}</span>;
  return <>{fmtValue(v, c.format)}</>;
}

function DataTable({ t, onNavigate }) {
  return (
    <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden" data-testid={`ted-table-${t.tool}`}>
      <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-slate-600">
        <Table2 className="w-3.5 h-3.5" /> {t.title}<span className="ml-auto font-normal text-slate-400">{t.truncated ? `showing ${t.rows.length} of ${t.total_rows}` : `${t.total_rows} row(s)`}</span>
      </div>
      <div className="overflow-x-auto max-h-80">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white"><tr>{t.columns.map((c) => <th key={c.key} className="px-3 py-1.5 text-left font-medium text-slate-500 whitespace-nowrap border-b">{c.label}</th>)}</tr></thead>
          <tbody>{t.rows.map((r, i) => <tr key={i} className="border-b last:border-0 hover:bg-slate-50/60">{t.columns.map((c) => <td key={c.key} className={`px-3 py-1.5 align-top text-slate-700 ${["gbp", "days", "number"].includes(c.format) ? "text-right tabular-nums whitespace-nowrap" : c.format === "date" ? "whitespace-nowrap" : ""}`}><Cell c={c} row={r} onNavigate={onNavigate} /></td>)}</tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

const KIND = { calculation: { icon: Calculator, cls: "text-cyan-700", label: "Calculated" }, hypothesis: { icon: HelpCircle, cls: "text-amber-700", label: "Unverified" } };

function Evidence({ s, idx, onNavigate }) {
  const [open, setOpen] = useState(false);
  const tools = s.evidence?.tools || [];
  if (!tools.length && !s.records.length) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-800" data-testid={`ted-evidence-toggle-${idx}`}>
        <ShieldCheck className="w-3.5 h-3.5" /> View evidence {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600 space-y-1.5" data-testid={`ted-evidence-${idx}`}>
          {tools.map((t, i) => <div key={i}><span className="font-semibold">{t.department || t.tool}</span> · live TedOps records · read {t.retrieved_at ? new Date(t.retrieved_at).toLocaleString("en-GB") : "now"}</div>)}
          {!!s.records.length && <div className="flex flex-wrap gap-1">{s.records.map((r) => <Link key={`${r.type}:${r.id}`} to={r.link || "#"} onClick={onNavigate} title={r.label} className="rounded bg-white border px-1.5 py-0.5 hover:border-[var(--ted-violet)]" data-testid={`ted-citation-${r.type}-${r.id}`}>{r.type} {r.id}</Link>)}</div>}
        </div>
      )}
    </div>
  );
}

function Section({ s, idx, onNavigate }) {
  const [open, setOpen] = useState(!s.supporting);
  const p = PRIO[s.priority] || PRIO.info;
  return (
    <section className="relative rounded-xl border border-slate-200 bg-white pl-4 pr-3 py-3 overflow-hidden" data-testid={`ted-section-${idx}`}>
      <span className={`absolute left-0 top-0 h-full w-1 ${p.bar}`} />
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid={`ted-section-toggle-${idx}`}>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="font-semibold text-sm text-slate-900">{s.title}</span>
        {s.department && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-[var(--ted-violet)]">{s.department}</span>}
        {s.priority !== "info" && <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${p.cls}`} data-testid={`ted-section-priority-${idx}`}>{p.label}</span>}
      </button>
      {open && (
        <div className="mt-2">
          {!!s.points.length && <ul className="space-y-1 text-[13px] leading-relaxed text-slate-700">{s.points.map((pt, i) => {
            const k = KIND[pt.kind];
            return <li key={i} className="flex gap-2">{k ? <k.icon className={`mt-0.5 w-3.5 h-3.5 shrink-0 ${k.cls}`} aria-label={k.label} /> : <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />}<span>{pt.text}{k && <span className={`ml-1 text-[10px] font-semibold ${k.cls}`}>{k.label}</span>}</span></li>;
          })}</ul>}
          {s.tables.map((t) => <DataTable key={t.tool} t={t} onNavigate={onNavigate} />)}
          <Evidence s={s} idx={idx} onNavigate={onNavigate} />
        </div>
      )}
    </section>
  );
}

export function TedAnswer({ st, onNavigate }) {
  return (
    <div className="space-y-3" data-testid="ted-structured-answer">
      <p className="text-sm leading-relaxed text-slate-800 font-medium" data-testid="ted-summary">{st.summary}</p>
      {!!st.warnings.length && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1" data-testid="ted-warnings">
          {st.warnings.map((w, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</div>)}
        </div>
      )}
      {!!st.metrics.length && <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2" data-testid="ted-metrics">{st.metrics.map((m, i) => <Metric key={i} m={m} i={i} />)}</div>}
      {st.sections.map((s, i) => <Section key={i} s={s} idx={i} onNavigate={onNavigate} />)}
      {!!st.next_steps.length && (
        <div className="rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-3" data-testid="ted-next-steps">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recommended next steps</div>
          <ul className="mt-1.5 space-y-1 text-[13px] text-slate-700">{st.next_steps.map((x, i) => <li key={i} className="flex gap-2"><ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-600" />{x.department && <span className="font-semibold">{x.department}:</span>} {x.action}</li>)}</ul>
          <p className="mt-2 text-[10px] text-slate-400">Ted is read-only. A person makes and approves any change in TedOps.</p>
        </div>
      )}
    </div>
  );
}
